import React, { useState, useEffect } from 'react'
import { Layout, Typography, Input, Table, Button, App, Empty } from 'antd'
import { ArrowRight, Plus, Search } from 'lucide-react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { Navigation } from '../../components/Navigation/Navigation'
import { usePageTitle } from '../../utils/pageTitle'
import { Supplier } from '../../types/ffe'
import { useApi } from '../../services/ApiContext'

const { Content } = Layout
const { Title } = Typography

export const SuppliersPage: React.FC = () => {
  const [suppliers, setSuppliers] = useState<Array<Supplier>>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const navigate = useNavigate()
  const { message } = App.useApp()
  const api = useApi()

  const loadSuppliers = async () => {
    try {
      setLoading(true)
      const data = await api.findSuppliers({
        query: {
          isArchived: false
        },
        $limit: 1000
      })
      setSuppliers(data.data)
    } catch (error) {
      console.error(error)
      message.error('Failed to load suppliers')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSuppliers()
  }, [])

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Supplier) => (
        <Link
          to={`/app/suppliers/${record._id}`}
          className="text-primary hover:text-primary-light font-semibold"
        >
          {text}
        </Link>
      )
    },
    {
      title: 'Contact',
      dataIndex: 'contact',
      key: 'contact',
      render: (contact?: Supplier['contact']) => contact?.name || '-'
    },
    {
      title: 'Email',
      dataIndex: ['contact', 'email'],
      key: 'email'
    },
    {
      title: 'Phone',
      dataIndex: ['contact', 'phone'],
      key: 'phone'
    },
    {
      title: 'Website',
      dataIndex: 'website',
      key: 'website',
      render: (website?: string) =>
        website ? (
          <Button
            type="link"
            href={website}
            target="_blank"
            rel="noopener noreferrer"
          >
            Visit website
          </Button>
        ) : (
          '-'
        )
    }
  ]

  usePageTitle('Suppliers')

  const filteredSuppliers = suppliers.filter((supplier) =>
    (
      supplier.name +
      supplier.address +
      supplier.name +
      supplier.contact?.email +
      supplier.contact?.phone
    )
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  )

  return (
    <Layout className="min-h-screen">
      <Navigation />
      <Content className="p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <Title level={4}>Suppliers</Title>
            <div className="flex gap-4">
              <Input
                prefix={<Search className="h-4 w-4 text-gray-400" />}
                placeholder="Search suppliers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-md"
              />
              <Button
                type="primary"
                icon={<Plus className="h-4 w-4" />}
                onClick={() => navigate('/app/suppliers/new')}
              >
                New Supplier
              </Button>
            </div>
          </div>
          <Table
            columns={columns}
            dataSource={filteredSuppliers}
            loading={loading}
            pagination={{ pageSize: 25 }}
            rowKey="_id"
            locale={{
              emptyText: (
                <Empty
                  className="mb-10"
                  description="Easily add suppliers while creating FF&E documents, you can later manage or search them here."
                >
                  <NavLink to="/app/editor">
                    <Button type="primary">
                      Create FF&E Specification{' '}
                      <ArrowRight className="h-4 w-4"></ArrowRight>
                    </Button>
                  </NavLink>
                </Empty>
              )
            }}
          />
        </div>
      </Content>
    </Layout>
  )
}
